import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Video, Clock, MessageCircle, Sparkles, Volume2, VolumeX, Maximize2, Play } from "lucide-react";
import { api, waLink } from "@/lib/api";
import { photoSrc } from "@/components/Reviews";

function Reel({ src }) {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  useEffect(() => { ref.current?.play?.().catch(() => {}); }, [src]);
  const fullscreen = () => { const v = ref.current; if (!v) return; (v.requestFullscreen || v.webkitEnterFullscreen)?.call(v); };
  return (
    <>
      <video ref={ref} src={photoSrc(src)} autoPlay muted={muted} loop playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover" data-testid="drone-addon-reel" />
      <div className="absolute top-4 right-4 flex gap-2">
        <button type="button" onClick={() => setMuted((m) => !m)} aria-label={muted ? "Unmute" : "Mute"} data-testid="drone-reel-mute" className="bg-ink/70 backdrop-blur text-white rounded-full p-2.5 hover:bg-terra transition-colors">{muted ? <VolumeX size={15} /> : <Volume2 size={15} />}</button>
        <button type="button" onClick={fullscreen} aria-label="Fullscreen" data-testid="drone-reel-fullscreen" className="bg-ink/70 backdrop-blur text-white rounded-full p-2.5 hover:bg-terra transition-colors"><Maximize2 size={15} /></button>
      </div>
    </>
  );
}

export default function DroneAddon() {
  const [addon, setAddon] = useState(null);
  const [active, setActive] = useState(0);
  useEffect(() => { api.get("/addons").then((r) => { const a = r.data.find((x) => x.id === "drone"); setAddon(a || null); setActive(a?.reel_url ? -1 : 0); }).catch(() => {}); }, []);
  if (!addon) return null;
  const price = Number(addon.price).toLocaleString("en-IN");
  const wa = waLink(`Hi Random Rider! I'd like to add the ${addon.name} (${addon.videos} aerial clips, ${addon.minutes} min total, ₹${price}) to my trip. When can we shoot?`);
  const showReel = active === -1 && addon.reel_url;
  return (
    <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="mt-12 grid grid-cols-1 lg:grid-cols-12 rounded-3xl overflow-hidden border border-ink/10 bg-white shadow-sm" data-testid="drone-addon-card">
      <div className="lg:col-span-7 relative aspect-[16/10] lg:aspect-auto lg:min-h-[380px] bg-ink">
        {showReel ? <Reel src={addon.reel_url} />
          : <img src={photoSrc(addon.images[active] || addon.images[0])} alt={`${addon.name} ${active + 1}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500" data-testid="drone-addon-photo" />}
        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent pointer-events-none" />
        <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-gold text-ink text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-full"><Sparkles size={12} /> {showReel ? "Sample reel" : "New add-on"}</span>
        <div className="absolute bottom-4 left-4 flex gap-2" data-testid="drone-addon-thumbs">
          {addon.reel_url && (
            <button type="button" onClick={() => setActive(-1)} aria-label="Play sample reel" data-testid="drone-addon-thumb-reel"
              className={`w-16 h-11 rounded-lg overflow-hidden border-2 grid place-items-center bg-ink/80 text-white transition-colors ${active === -1 ? "border-gold" : "border-white/60 hover:border-white"}`}><Play size={16} className="fill-white" /></button>
          )}
          {addon.images.map((src, i) => (
            <button key={src} type="button" onClick={() => setActive(i)} aria-label={`Photo ${i + 1}`} data-testid={`drone-addon-thumb-${i + 1}`}
              className={`w-16 h-11 rounded-lg overflow-hidden border-2 transition-colors ${active === i ? "border-gold" : "border-white/60 hover:border-white"}`}>
              <img src={photoSrc(src)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
      <div className="lg:col-span-5 p-7 sm:p-9 flex flex-col">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-terra">{addon.tagline}</p>
        <h3 className="font-serif text-3xl sm:text-4xl font-bold mt-2 leading-tight" data-testid="drone-addon-name">{addon.name}</h3>
        <p className="mt-4 text-sm text-ink/70 leading-relaxed">{addon.description}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <span className="inline-flex items-center gap-1.5 bg-ink/5 px-3.5 py-2 rounded-full text-sm" data-testid="drone-addon-videos"><Video size={14} className="text-terra" /> {addon.videos} aerial clips</span>
          <span className="inline-flex items-center gap-1.5 bg-ink/5 px-3.5 py-2 rounded-full text-sm" data-testid="drone-addon-minutes"><Clock size={14} className="text-terra" /> {addon.minutes} min total</span>
        </div>
        <div className="mt-6 flex items-end gap-3">
          <p className="font-serif text-5xl font-black text-terra" data-testid="drone-addon-price">₹{price}</p>
          <p className="text-xs text-ink/50 pb-2">per shoot · add to any ride, tour or package</p>
        </div>
        <p className="mt-3 text-[11px] text-ink/45 leading-relaxed" data-testid="drone-addon-conditions">{addon.conditions}</p>
        <a href={wa} target="_blank" rel="noreferrer" data-testid="drone-addon-whatsapp-button"
          className="mt-6 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold py-3.5 rounded-full hover:brightness-110 transition-all">
          <MessageCircle size={16} /> Book the drone shoot
        </a>
      </div>
    </motion.div>
  );
}
